import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { SITE } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];

function Line({ children, delay = 0 }) {
  return (
    <span className="reveal-line-wrap">
      <motion.span
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.2, ease: EASE, delay }}
        className="block will-change-transform"
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function HeroSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 240]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 0.85]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  // Mouse parallax for hero product
  const productRef = useRef(null);
  useEffect(() => {
    const el = productRef.current;
    if (!el) return;
    const handler = (e) => {
      const { innerWidth: w, innerHeight: h } = window;
      const x = (e.clientX / w - 0.5) * 18;
      const y = (e.clientY / h - 0.5) * 18;
      el.style.setProperty("--mx", `${x}px`);
      el.style.setProperty("--my", `${y}px`);
    };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, []);

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden pt-28 pb-16"
    >
      {/* Cinematic radial glow */}
      <motion.div
        aria-hidden
        style={{ y: glowY }}
        className="pointer-events-none absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] max-w-none studio-glow opacity-90"
      />

      {/* GE logo watermark behind the hero */}
      <motion.img
        aria-hidden
        src={`${process.env.PUBLIC_URL || ""}/assets/logo/ge.png`}
        style={{ y: glowY }}
        className="pointer-events-none absolute right-[-6%] top-[52%] -translate-y-1/2 w-[85vw] max-w-[820px] opacity-[0.05] select-none"
        alt=""
      />

      {/* Subtle grid layer */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,.5) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          maskImage:
            "radial-gradient(circle at 50% 40%, black 30%, transparent 75%)",
        }}
      />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10">
        {/* Top eyebrow row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
          className="flex flex-wrap items-center justify-between gap-4"
        >
          <span className="inline-flex items-center gap-2.5 rounded-full glass px-4 py-2 text-[11px] uppercase tracking-[0.16em] text-white/80">
            <span className="h-1.5 w-1.5 rounded-full bg-[#22d3ee] shadow-[0_0_10px_#22d3ee]" />
            <span className="font-mono-tag" data-testid="hero-eyebrow">
              Nanded's Trusted Electrical Store
            </span>
          </span>
        </motion.div>

        {/* Kinetic Headline — v34 tagline */}
        <div className="relative mt-14 md:mt-24">
          <motion.div style={{ y: textY }}>
            <h1
              data-testid="hero-heading"
              className="font-display text-white leading-[0.92] tracking-tighter text-[14vw] md:text-[8.5vw] lg:text-[7.6vw] font-extrabold"
            >
              <Line delay={0.15}>Every Wire.</Line>
              <Line delay={0.3}>Every Switch.</Line>
              <Line delay={0.45}>
                <span className="text-grad-brand italic font-black">
                  Done Right.
                </span>
              </Line>
            </h1>
          </motion.div>

          {/* Floating hero product */}
          <motion.div
            style={{ y: heroY, scale: heroScale }}
            className="pointer-events-none absolute right-0 -top-6 md:-right-2 md:-top-8 lg:right-0 lg:-top-8 w-[28vw] max-w-[280px] aspect-square"
          >
            <div
              ref={productRef}
              className="relative w-full h-full"
              style={{
                transform: "translate3d(var(--mx,0px), var(--my,0px), 0)",
                transition: "transform 250ms cubic-bezier(.22,1,.36,1)",
              }}
            >
              <motion.div
                animate={{ y: [0, -18, 0], rotate: [0, 2.5, -2.5, 0] }}
                transition={{
                  duration: 7,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative w-full h-full"
              >
                {/* Contact shadow */}
                <div className="absolute left-1/2 -translate-x-1/2 bottom-[-6%] w-[70%] h-[36px] rounded-[50%] bg-[#1d52c4] opacity-70 blur-2xl" />
                {/* Radial back glow */}
                <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_center,rgba(34,211,238,0.5),transparent_60%)] blur-2xl" />
                <img
                  data-testid="hero-product-image"
                  src="https://images.unsplash.com/photo-1518552620125-dfc3c2a8e974?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxsZWQlMjBsaWdodCUyMGJ1bGIlMjBnbG93aW5nJTIwZGFya3xlbnwwfHx8fDE3ODI4MDY1Mjh8MA&ixlib=rb-4.1.0&q=85"
                  alt="Hanging bulb — Giriraj Enterprises"
                  className="relative z-10 h-full w-full object-cover rounded-[36px] shadow-[0_50px_120px_-30px_rgba(34,211,238,0.35)] border border-white/10"
                  style={{ mixBlendMode: "screen" }}
                  loading="eager"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Under-hero row: subtitle + CTA + Meta */}
        <div className="mt-16 md:mt-24 grid md:grid-cols-12 gap-10 items-end relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.85 }}
            className="md:col-span-7"
          >
            <p className="max-w-[52ch] text-white/70 text-[15px] md:text-lg leading-relaxed">
              Premium fans, Anchor by Panasonic switches, LED lights, wires &
              more — all under one roof at Gurudwara Road, Nanded.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                data-testid="hero-cta-primary"
                href="#products"
                className="group inline-flex items-center gap-2 rounded-full grad-bg text-white pl-6 pr-2 py-2 text-[13px] font-semibold hover:opacity-95 transition-opacity duration-300 shadow-[0_20px_60px_-20px_rgba(29,82,196,0.6)]"
              >
                Our Products
                <span className="ml-1 grid h-9 w-9 place-items-center rounded-full bg-white text-[#0d1117]">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </a>
              <a
                data-testid="hero-cta-secondary"
                href={`https://wa.me/${SITE.phoneRaw}?text=${SITE.waIntro}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  const url = `https://wa.me/${SITE.phoneRaw}?text=${SITE.waIntro}`;
                  const w = window.open(url, "_blank", "noopener,noreferrer");
                  if (!w) window.location.href = url;
                }}
                className="inline-flex items-center gap-2 rounded-full bg-[#25d366] text-white px-6 py-3 text-[13px] font-semibold hover:bg-[#22c55e] transition-colors duration-300 shadow-[0_20px_60px_-20px_rgba(37,211,102,0.6)]"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp Us
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 1.0 }}
            className="md:col-span-5"
          >
            <div className="grid grid-cols-3 gap-3 md:gap-4">
              {SITE.stats.map((s, i) => (
                <div
                  key={i}
                  className="glass rounded-2xl px-4 py-5 hover:border-white/20 transition-colors duration-500 text-center"
                >
                  <div className="font-display text-3xl md:text-4xl text-grad-brand">
                    {s.k}
                  </div>
                  <div className="mt-2 text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-white/55">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* End of hero */}
      </div>
    </section>
  );
}
